import React, { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { triggerNotification } from '../../../helpers/notification';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import PrintDesignEditor from '../designer/PrintDesignEditor';

const VendorProductDesignView = ({}) => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const productUuids = searchParams.get('ids')?.split(',') ?? [];
  const includeAllDesigns = searchParams.get('allDesigns') === 'true';
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchVendorProductPrintDesigns,
    fetchVendorProductPrintDesignOptions,
    createVendorProductsFromPrintTemplates,
    createVendorDesigns
  } = useContext(VendorContext);

  const { ecomVendorProducts, ecomExternalProducts } =
    state.ecomVendorProductPrintDesignEdit.value || {};
  const loading =
    state.ecomVendorProductPrintDesignEdit.loading || !hasFetched.current;

  useEffect(() => {
    if (authState.pkEcomVendor) {
      fetchVendorProductPrintDesigns(
        authState.pkEcomVendor,
        {
          uuids: productUuids,
          includeAllDesigns
        },
        null,
        (error) => {
          triggerNotification(error);
        }
      );

      fetchVendorProductPrintDesignOptions(authState.pkEcomVendor);
      hasFetched.current = true;
    }
  }, [authState.pkEcomVendor]);

  return (
    <PrintDesignEditor
      ecomExternalProducts={
        ecomExternalProducts?.map((p) => ({
          id: p.productExternalId,
          ...p.ecomVendorProduct
        })) || []
      }
      fkEcomVendor={authState.pkEcomVendor}
      loading={loading}
      onCreateDesigns={({ designs }, onSuccessCallback, onErrorCallback) => {
        createVendorDesigns(
          authState.pkEcomVendor,
          {
            ecomVendorDesigns: designs.map((d) => ({
              ...d,
              fkEcomVendorMedia: d.pkMedia
            }))
          },
          (data) => {
            onSuccessCallback(
              data.map((d) => ({
                pkDesign: d.pkEcomVendorDesign,
                pkMedia: d.fkEcomVendorMedia,
                name: d.name
              }))
            );
          },
          onErrorCallback
        );
      }}
      onSaveChanges={(
        { productDesigns },
        onSuccessCallback,
        onErrorCallback
      ) => {
        createVendorProductsFromPrintTemplates(
          authState.pkEcomVendor,
          {
            productDesigns: productDesigns.map((d) => ({
              ...d,
              fkEcomVendorProduct: d.pkProduct,
              fkEcomVendorDesign: d.pkDesign
            }))
          },
          (data) => {
            const product = data[0];
            const printDesign = product.ecomVendorProductPrintDesigns[0];

            onSuccessCallback({
              id: product.uuid,
              pkPrintDesign: printDesign.pkEcomVendorProductPrintDesign,
              pkProduct: product.pkEcomVendorProduct,
              productName: product.name,
              fkEcomExternalConnectionType:
                product.ecomVendorProductExternalIds[0]
                  ?.fkEcomExternalConnectionType,
              printDesignPreview: printDesign?.printImageUrl,
              canvasState: {
                items: printDesign.ecomVendorProductPrintDesignItems.map(
                  (i) => ({
                    ...i,
                    id: i.pkEcomVendorProductPrintDesignItem.toString(),
                    pkDesignItem: i.pkEcomVendorProductPrintDesignItem,
                    visible: !i.hidden
                  })
                )
              }
            });
          },
          onErrorCallback
        );
      }}
      onSaveChangesComplete={(printDesigns) => {
        triggerNotification('Print designs saved!', 'success');

        const allIds = [
          ...new Set([...productUuids, ...printDesigns.map((d) => d.id)])
        ];

        const queryString = `ids=${allIds.join(',')}${
          includeAllDesigns ? '&allDesigns=true' : ''
        }`;

        navigate(`/vendor/products/design?${queryString}`);
      }}
      options={{
        designs:
          state.ecomVendorProductPrintDesignOptions.value?.ecomVendorDesigns.map(
            (d) => ({
              pkDesign: d.pkEcomVendorDesign,
              name: d.name,
              pkMedia: d.fkEcomVendorMedia
            })
          ) || []
      }}
      printDesigns={
        ecomVendorProducts
          ?.map((p) => {
            const externalConnection = p.ecomVendorProductExternalIds[0];

            const externalProduct = ecomExternalProducts.find(
              (ep) =>
                ep.fkEcomExternalProduct ===
                externalConnection.fkEcomExternalProduct
            );

            return externalConnection
              ? {
                  id: p.uuid,
                  pkProduct: p.pkEcomVendorProduct,
                  productName: p.name,
                  productId:
                    externalConnection.fkEcomExternalProduct?.toString() ||
                    externalConnection.externalId,
                  fkEcomExternalConnectionType:
                    externalConnection.fkEcomExternalConnectionType,
                  removedVariantIds: externalProduct.ecomVendorProduct.ecomVendorProductVariants
                    .filter(
                      (v) =>
                        !p.ecomVendorProductVariants.some((pv) =>
                          pv.ecomVendorProductExternalIds.some(
                            (pd) =>
                              pd.fkEcomExternalProductVariant ===
                              v.pkEcomExternalProductVariant
                          )
                        )
                    )
                    .map((v) => v.uuid),
                  designs: p.ecomVendorProductPrintDesigns.map((d) => ({
                    pkPrintDesign: d.pkEcomVendorProductPrintDesign,
                    printDesignPreview: d.printImageUrl,
                    printImageUrl: d.printImageUrl,
                    fkEcomImagePosition: d.fkEcomImagePosition,
                    variantIds:
                      d.ecomVendorProductPrintDesignVariants
                        .map((v) =>
                          v.ecomVendorProductExternalIds
                            .find(
                              (i) =>
                                i.fkEcomExternalConnectionType ===
                                externalConnection.fkEcomExternalConnectionType
                            )
                            ?.fkEcomExternalProductVariant?.toString()
                        )
                        .filter(Boolean) || [],
                    canvasState: d
                      ? {
                          items: d.ecomVendorProductPrintDesignItems.map(
                            (i) => ({
                              ...i,
                              id: i.pkEcomVendorProductPrintDesignItem.toString(),
                              pkDesignItem:
                                i.pkEcomVendorProductPrintDesignItem,
                              visible: !i.hidden,
                              itemPropertiesList: i.itemProperties
                            })
                          )
                        }
                      : null
                  }))
                }
              : null;
          })
          .filter((f) => !!f) || []
      }
      productIds={productUuids}
    />
  );
};

VendorProductDesignView.propTypes = {};

export default VendorProductDesignView;
